import {
  VendorProfile,
  VendorsQuery,
  VendorsQueryVariables,
} from "@/__generated__/graphql";
import Avatar from "@/components/common/Avatar";
import Error from "@/components/common/Error";
import { Loader } from "@/components/common/Loader";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { gql, useQuery } from "@apollo/client";
import { find } from "lodash";
import { Asterisk, MapPin, Minus, Plus, X } from "lucide-react";
import { useState } from "react";

export type Vendor = NonNullable<
  NonNullable<VendorsQuery["vendors"]>["edges"]
>[0]["node"];

type Props = {
  onNext: (vendors: Vendor[]) => void;
  vendorIds?: string[];
  forSubcontracting?: boolean;
  limit: number;
  excludedVendorIds?: string[];
  projectId?: string;
  isSubmitting?: boolean;
  vendorService?: { name: string; id: string };
  vendorServices?: { id: string; name: string }[];
  onClose: () => void;
  error?: string | null;
};

function SelectVendors({
  onClose,
  onNext,
  projectId,
  vendorService,
  vendorServices,
  excludedVendorIds,
  limit,
  isSubmitting,
  error,
}: Props) {
  const variables: VendorsQueryVariables = {
    projectId,
  };
  if (vendorService) {
    variables.serviceId = vendorService.id;
  } else if (vendorServices && vendorServices.length > 0) {
    variables.serviceIds = vendorServices.map((v) => v.id);
  }

  const { data, loading } = useQuery<VendorsQuery, VendorsQueryVariables>(
    VENDORS_QUERY,
    { variables },
  );

  const vendors = data?.vendors?.edges
    .map((edge) => edge.node)
    .filter((v) => v.id && !excludedVendorIds?.includes(v.id));

  const [selectedVendors, setSelectedVendors] = useState<Vendor[]>([]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="min-w-250 p-0 text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !vendors?.length ? (
          <div>
            {loading && <Loader />}

            {!loading && <div>No vendors found.</div>}
          </div>
        ) : (
          <div>
            <div className="flex p-4 gap-4">
              <div className="flex-1">
                <div className="text-xl font-medium">Select Vendors</div>

                <div className="max-h-100 overflow-scroll mt-5">
                  <div className="flex flex-col gap-5">
                    {vendors.map((vendor) => {
                      const isSelected = vendor
                        ? !!find(selectedVendors, (v) => v.id === vendor.id)
                        : false;
                      const profile = vendor.profile as VendorProfile;
                      const office =
                        vendor.offices.length > 0 && vendor.offices[0];
                      const country =
                        office && office.country && office.country.name;
                      const serviceCapacities = profile.serviceCapacities;

                      let numSeats: string | null | undefined = "";
                      if (serviceCapacities) {
                        const serviceCapacity = serviceCapacities.find(
                          (item) => {
                            return vendorService?.id === item.service.id;
                          },
                        );
                        numSeats = serviceCapacity?.numSeatsDisplay;
                      }

                      return (
                        <div
                          key={vendor.id}
                          className="flex items-center justify-between border rounded-xl p-4 cursor-pointer"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedVendors(
                                selectedVendors.filter(
                                  (v) => v.id !== vendor.id,
                                ),
                              );
                            } else {
                              setSelectedVendors(
                                [vendor, ...selectedVendors].slice(0, limit),
                              );
                            }
                          }}
                        >
                          <Avatar
                            name={profile?.companyName}
                            url={profile?.logoUrl}
                            avatarSize={32}
                            className="rounded-md"
                          />

                          <div className="font-medium w-50">
                            <LongContentWrapper>
                              {profile?.companyName}
                            </LongContentWrapper>
                          </div>

                          <div className="flex items-center gap-1">
                            <MapPin className="text-secondary-icon" size={14} />
                            <span className="text-secondary-icon text-xs font-medium">
                              Location
                            </span>
                            <span className="text-sm font-medium w-30">
                              <LongContentWrapper>{country}</LongContentWrapper>
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="text-secondary-icon border border-secondary-icon stroke-2 w-fit rounded-[3px]">
                              <Asterisk size={12} />
                            </div>
                            <span className="text-secondary-icon font-medium text-xs">
                              Capacity
                            </span>

                            <span className="ml-2 w-20">
                              {numSeats || "--"}
                            </span>
                          </div>

                          <div
                            className={cn(
                              "p-2 rounded-sm border border-transparent",
                              isSelected
                                ? "border-accent-foreground"
                                : "bg-service-paint text-white",
                            )}
                          >
                            {isSelected ? (
                              <Minus size={14} />
                            ) : (
                              <Plus size={14} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="w-60">
                <div className="font-medium text-xl">Vendors Selected</div>
                <div className="text-secondary-icon mt-1">
                  Max {limit} vendors can be selected
                </div>

                <div className="mt-4 max-h-97 overflow-scroll">
                  <div className="flex flex-col gap-3">
                    {selectedVendors.map((vendor) => {
                      return (
                        <Button
                          className="justify-between text-base py-3 h-fit bg-tertiary hover:bg-tertiary/90"
                          onClick={() => {
                            setSelectedVendors(
                              selectedVendors.filter((v) => v.id !== vendor.id),
                            );
                          }}
                        >
                          {(vendor.profile as VendorProfile)?.companyName}
                          <X />
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex justify-end gap-4 p-4">
              <Error error={error} />

              <Button onClick={onClose}>Cancel</Button>

              <Button
                className="bg-service-paint hover:bg-service-paint/96 text-white"
                onClick={() => {
                  onNext(selectedVendors);
                }}
                disabled={!selectedVendors.length || isSubmitting}
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const VENDOR_FRAGMENT = gql`
  fragment Vendor on Company {
    __typename
    id
    isUserFavorite
    profile {
      ... on VendorProfile {
        id
        commissionPercentage
        companyName
        isDefault
        highlights {
          id
          name
          icon
          iconUrl
        }
        logoUrl
        certifications {
          id
          name
        }
        keyStaff {
          id
          name
          title
          url
        }
        serviceCapacities {
          numSeatsDisplay
          service {
            id
          }
        }
        locations {
          city
        }
        yearsInBusiness
        website
        showreelUrl
      }
    }
    offices {
      id
      country {
        id
        name
      }
      state
      city
      zipcode
      officeHours {
        id
        day
        is24hrDay
        closeTime
        openTime
      }
    }
  }
`;

export const VENDORS_QUERY = gql`
  query Vendors(
    $serviceId: SmartID
    $projectId: SmartID
    $certifications: [SmartID]
    $serviceIds: [SmartID]
  ) {
    vendors(
      serviceId: $serviceId
      projectId: $projectId
      certifications: $certifications
      serviceIds: $serviceIds
    ) {
      edges {
        node {
          ...Vendor
        }
      }
    }
  }
  ${VENDOR_FRAGMENT}
`;

export default SelectVendors;
